import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Text,
  Group,
  Badge,
  Divider,
  Stack,
  Button,
  Modal,
  Anchor,
  List,
} from '@mantine/core';
import { IconPlus, IconBriefcase } from '@tabler/icons-react';
import logger from '@ayocore/platform/common/helpers/logger';
import type { IncomingEmail } from '../../schemas/incoming-email.schema';
import type { EmailAttachment } from '../../schemas/email-attachment.schema';
import { CreateLeadForm } from '@ayocore/erp/crm/client/components/Leads/forms';
import { useIncomingEmailRepository } from '@ayocore/mailer/client/rpc';
import { leadStatusRepository, useLeadRepository } from '@ayocore/erp/crm/client/rpc';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import { apiBaseUrl } from '@ayocore/platform/client/helpers/fetcher';
import { LeadView } from '@ayocore/erp/crm/client/components/Leads/views/LeadView';

interface EmailViewProps {
  email: IncomingEmail | null;
  loading?: boolean;
  onRefresh?: () => void;
}

const SOURCE_TYPE_EMAIL = 'mailer::IncomingEmailRepository';

const EmailView = ({ email, loading = false, onRefresh }: EmailViewProps) => {
  const [showCreateLeadForm, setShowCreateLeadForm] = useState(false);
  const [newStatusId, setNewStatusId] = useState<number | undefined>(undefined);
  const [localStatus, setLocalStatus] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [associatedLeads, setAssociatedLeads] = useState<Array<{ id: number; title: string }>>([]);

  const incomingEmailRepository = useIncomingEmailRepository();
  const leadRepository = useLeadRepository();
  const leadDrawer = useDrawer(LeadView);

  // TODO: исправить говнокод с получением статуса
  // Load "Новый" status ID
  useEffect(() => {
    const loadNewStatus = async () => {
      try {
        const statuses = await leadStatusRepository.limit(100);
        const newStatus = statuses.find(s => s.name === 'Новый');
        if (newStatus) {
          setNewStatusId(newStatus.id);
        }
      } catch (error) {
        logger.error('Failed to load statuses:', error);
      }
    };
    loadNewStatus();
  }, []);

  const loadAssociatedLeads = useCallback(async () => {
    if (!email?.uuid) return;
    try {
      const leads = await leadRepository
        .where('source_entity_type', SOURCE_TYPE_EMAIL)
        .where('source_entity_uuid', email.uuid)
        .fetch();
      setAssociatedLeads((leads ?? []).map(l => ({ id: l.id, title: l.title || `Лид #${l.id}` })));
    } catch (error) {
      logger.error('[EmailView] Не удалось загрузить связанные лиды', error);
      setAssociatedLeads([]);
    }
  }, [email?.uuid, leadRepository]);

  const handleCreateLead = async () => {
    setShowCreateLeadForm(false);
    try {
      if (email?.id) {
        await incomingEmailRepository.setStatus(email.id, 'lead');
        setLocalStatus('lead');
        await loadAssociatedLeads();
        onRefresh?.();
      }
    } catch (e) {
      logger.error('Failed to set email status to lead', e);
    }
  };

  // Sync localStatus when email changes
  useEffect(() => {
    setLocalStatus(email?.status || null);
  }, [email]);

  // Load attachments when email changes
  useEffect(() => {
    const loadAttachments = async () => {
      if (!email?.id) {
        setAttachments([]);
        return;
      }

      setLoadingAttachments(true);
      try {
        const emailAttachments = await incomingEmailRepository.getAttachments(email.id);
        setAttachments(emailAttachments);
      } catch (error) {
        logger.error('Failed to load attachments:', error);
        setAttachments([]);
      } finally {
        setLoadingAttachments(false);
      }
    };

    loadAttachments();
  }, [email?.id]);

  // Загрузка лидов, созданных из этого письма
  useEffect(() => {
    if (!email?.uuid) {
      setAssociatedLeads([]);
      return;
    }
    void loadAssociatedLeads();
  }, [email?.uuid, loadAssociatedLeads]);

  if (loading || !email) {
    return (
      <Card padding='md' shadow='sm' style={{ height: '100%' }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Text size='xs' c='dimmed'>
              Загрузка письма…
            </Text>
          </div>
        ) : (
          <Text c='dimmed' ta='center' size='sm'>
            Выберите письмо для просмотра
          </Text>
        )}
      </Card>
    );
  }

  // const statusColors = {
  //   new: 'blue',
  //   processed: 'green',
  //   failed: 'red'
  // };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Get file icon based on content type
  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType.includes('pdf')) return '📄';
    if (contentType.includes('word') || contentType.includes('document')) return '📝';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
    if (contentType.includes('zip') || contentType.includes('rar')) return '📦';
    return '📎';
  };

  return (
    <Card padding='md' shadow='sm'>
      <Stack gap='md'>
        {/* Header */}
        <Group justify='space-between' align='center' wrap='nowrap'>
          <Group gap='xs' align='center'>
            <Text fw={600} size='lg'>
              {email.subject || '(без темы)'}
            </Text>
            {localStatus === 'lead' && (
              <Badge size='xs' variant='outline' color='green'>
                Заявка
              </Badge>
            )}
            {localStatus === 'declined' && (
              <Badge size='xs' variant='outline' color='red'>
                Отклонено
              </Badge>
            )}
          </Group>
          <Group gap='xs'>
            {localStatus !== 'lead' && (
              <Button
                size='xs'
                leftSection={<IconPlus size={12} />}
                onClick={() => setShowCreateLeadForm(true)}
              >
                Создать заявку
              </Button>
            )}
            {localStatus !== 'lead' && localStatus !== 'declined' && (
              <Button
                size='xs'
                variant='light'
                color='red'
                onClick={async () => {
                  if (!email?.id) return;
                  try {
                    await incomingEmailRepository.setStatus(email.id, 'declined');
                    setLocalStatus('declined');
                    onRefresh?.();
                  } catch (e) {
                    logger.error('Failed to set email status to declined', e);
                  }
                }}
              >
                Отклонить
              </Button>
            )}
          </Group>
        </Group>

        <Group justify='space-between' align='center'>
          <Text size='xs'>
            <Text span fw={500} size='xs'>
              От кого:{' '}
            </Text>
            {email.from_name ? `${email.from_name} <${email.from_email}>` : email.from_email}
          </Text>
          <Text size='xs'>
            <Text span fw={500} size='xs'>
              Дата:{' '}
            </Text>
            {new Date(email.received_at as string).toLocaleString('ru-RU')}
          </Text>
        </Group>

        {/* Связанные лиды */}
        {associatedLeads.length > 0 && (
          <div>
            <Text fw={500} size='xs' mb='xs'>
              Связанные лиды:
            </Text>
            <Group gap='xs'>
              {associatedLeads.map(lead => (
                <Anchor
                  key={lead.id}
                  size='xs'
                  href='#'
                  onClick={e => {
                    e.preventDefault();
                    leadDrawer.open({
                      leadId: lead.id,
                      isModal: true,
                      title: lead.title,
                    });
                  }}
                >
                  <Group gap={4}>
                    <IconBriefcase size={14} />
                    {lead.title}
                  </Group>
                </Anchor>
              ))}
            </Group>
          </div>
        )}

        {/* Attachments */}
        {email.attachments_count > 0 && (
          <div>
            <Text fw={500} size='xs' mb='xs'>
              Вложения:
            </Text>
            {loadingAttachments ? (
              <Text size='xs' c='dimmed'>
                Загрузка вложений...
              </Text>
            ) : attachments.length > 0 ? (
              <List size='xs' spacing='xs'>
                {attachments.map((attachment, index) => (
                  <List.Item
                    key={attachment.id || index}
                    icon={getFileIcon(attachment.content_type)}
                  >
                    <Group gap='xs' align='center'>
                      <Anchor
                        href={`${apiBaseUrl}/api/mailer/attachment/${attachment.id}/download`}
                        target='_blank'
                        rel='noopener noreferrer'
                        size='xs'
                        style={{ textDecoration: 'none' }}
                      >
                        {attachment.filename}
                      </Anchor>
                      <Text size='xs' c='dimmed'>
                        ({formatFileSize(attachment.size_bytes)})
                      </Text>
                    </Group>
                  </List.Item>
                ))}
              </List>
            ) : (
              <Text size='xs' c='dimmed'>
                {email.attachments_count} файл(ов) - детали недоступны
              </Text>
            )}
          </div>
        )}

        <Divider />

        {/* HTML Body */}
        <div>
          {email.body_html ? (
            <EmailBodyFrame html={email.body_html} />
          ) : (
            <Text c='dimmed' size='xs'>
              HTML содержимое недоступно
            </Text>
          )}
        </div>
      </Stack>

      {/* Create Lead Modal */}
      <Modal
        opened={showCreateLeadForm}
        onClose={() => setShowCreateLeadForm(false)}
        title='Создать заявку из письма'
        size='lg'
      >
        <CreateLeadForm
          onLeadCreated={handleCreateLead}
          emailId={email.id}
          initialData={{
            title: `${email.from_name || email.from_email} - ${email.subject || '(без темы)'}`,
            description: email.body_text || '',
            lead_status_id: newStatusId,
            notes: `Источник: почта\nОт: ${email.from_name || ''} <${email.from_email}>\nДата: ${new Date(email.received_at as string).toLocaleString('ru-RU')}`,
          }}
        />
      </Modal>
    </Card>
  );
};

/**
 * EmailBodyFrame — изолированный iframe для безопасного рендера HTML письма.
 * Авто-ресайз по содержимому через postMessage, без скриптов, с базовыми стилями.
 */
function EmailBodyFrame({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(400);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Проверяем, что сообщение от нашего iframe
      if (event.data?.type === 'iframe-height' && typeof event.data.height === 'number') {
        const newHeight = Math.min(Math.max(event.data.height + 20, 200), 800);
        setHeight(newHeight);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Скрипт внутри iframe для отправки высоты через postMessage
  const heightScript = `
    <script>
      (function() {
        function sendHeight() {
          const height = document.body.scrollHeight;
          window.parent.postMessage({ type: 'iframe-height', height: height }, '*');
        }
        
        // Отправляем высоту сразу и после загрузки ресурсов
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', sendHeight);
        } else {
          sendHeight();
        }
        
        window.addEventListener('load', function() {
          sendHeight();
          // Повторяем через 300мс для изображений с ленивой загрузкой
          setTimeout(sendHeight, 300);
        });
        
        // Отслеживаем изменения размера
        if (window.ResizeObserver && document.body) {
          const observer = new ResizeObserver(sendHeight);
          observer.observe(document.body);
        }
      })();
    </script>
  `;

  const wrappedHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <base target="_blank">
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 12px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            font-size: 13px;
            line-height: 1.5;
            color: #212529;
            background: #f8f9fa;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          img { max-width: 100%; height: auto; }
          a { color: #228be6; text-decoration: none; }
          a:hover { text-decoration: underline; }
          table { border-collapse: collapse; max-width: 100%; }
          pre { white-space: pre-wrap; background: #fff; padding: 8px; border-radius: 4px; }
        </style>
        ${heightScript}
      </head>
      <body>${html}</body>
    </html>
  `;

  return (
    <iframe
      ref={iframeRef}
      srcDoc={wrappedHtml}
      sandbox='allow-scripts allow-popups allow-popups-to-escape-sandbox'
      referrerPolicy='no-referrer'
      style={{
        width: '100%',
        height: `${height}px`,
        border: '1px solid #e9ecef',
        borderRadius: '4px',
        backgroundColor: '#f8f9fa',
        transition: 'height 0.2s ease',
      }}
      title='Email content'
    />
  );
}

export default EmailView;
